import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, usePoll } from "../api";
import type { Device } from "../api";
import deviceThumb from "../assets/device-thumb.webp";
import lounge900 from "../assets/lounge-900.webp";
import lounge from "../assets/lounge.webp";
import { Icon } from "../components/icons";
import { fmtAgo } from "../format";

export function Devices() {
  const { data, error, reload } = usePoll<Device[]>("/api/devices", 10_000);

  if (error) return <p className="error">{error}</p>;
  if (!data) return <div className="spinner" />;

  const online = data.filter((d) => d.online).length;

  return (
    <section className="devices-page">
      <picture className="devices-bg" aria-hidden>
        <source media="(max-width: 900px)" srcSet={lounge900} />
        <img src={lounge} alt="" />
      </picture>

      <div className="devices-main">
        <header className="devices-hero">
          <div className="eyebrow">Devices</div>
          <h1>Your BotherLog devices</h1>
          <p>Manage your devices, check their status and keep them listening so you never miss a thing.</p>
          <Link to="/claim" className="cta cta-small"><Icon.Plus /> Add a device</Link>
        </header>

        <div className="stat-tiles">
          <div className="stat">
            <span className="stat-icon round"><Icon.Box /></span>
            <span>
              <strong>{data.length}</strong>
              <span className="muted">{data.length === 1 ? "Device connected" : "Devices connected"}</span>
            </span>
          </div>
          <div className="stat">
            <span className="stat-icon round"><Icon.Wave /></span>
            <span>
              <strong>{online ? "Listening" : "Not listening"}</strong>
              <span className="muted">{online} {online === 1 ? "device" : "devices"} active</span>
            </span>
          </div>
        </div>

        {data.length === 0 ? (
          <div className="devices-shell empty">
            <img src={deviceThumb} alt="" width={160} />
            <h3>No devices yet</h3>
            <p className="muted">Plug yours in, then add it with the code on its label.</p>
            <Link to="/claim" className="cta cta-small"><Icon.Plus /> Add your BotherLog</Link>
          </div>
        ) : (
          <div className="devices-shell">
            {data.map((d) => <DeviceCard key={d.id} device={d} onChanged={reload} />)}
          </div>
        )}

        <div className="help-tiles">
          <HelpTile to="/claim" icon={<Icon.Gear />} title="Set up a new device">
            Follow our simple guide to get your BotherLog up and running.
          </HelpTile>
          <HelpTile to="/help" icon={<Icon.Book />} title="Help & support">
            Troubleshooting, FAQs and device information.
          </HelpTile>
          <HelpTile to="/shop" icon={<Icon.Cart />} title="Need another device?">
            Cover more areas of your home with an additional BotherLog.
          </HelpTile>
        </div>
      </div>
    </section>
  );
}

function DeviceCard({ device: d, onChanged }: { device: Device; onChanged: () => void }) {
  const navigate = useNavigate();
  const trained = Boolean(d.profile?.current_version);
  const [renaming, setRenaming] = useState(false);

  return (
    <article className="device-card">
      <div className="device-card-top">
        <span className={`online-dot ${d.online ? "on" : "off"}`} title={d.online ? "Online" : "Offline"} />
        <img className="device-thumb" src={deviceThumb} alt="" width={80} />
        <div className="device-card-id">
          {renaming ? (
            <RenameForm device={d} onDone={() => { setRenaming(false); onChanged(); }} />
          ) : (
            <Link to={`/devices/${d.id}`} className="device-card-name">{d.name ?? d.id}</Link>
          )}
          <div className="muted small">{d.id} · firmware {d.fw_version ?? "—"}</div>
        </div>
        <div className="device-card-actions">
          <span className={`status-pill ${d.online ? "on" : "off"}`}>
            <span className="pill-icon"><Icon.Wave size={14} /></span>
            {d.online ? "Listening" : "Offline"}
            <span className="pill-extra">· {trained ? d.profile!.label.toLowerCase() : "not trained yet"}</span>
          </span>
          <button className="ghost" onClick={() => navigate(`/devices/${d.id}`)}><Icon.Gear size={18} /> Settings</button>
          <DeviceMenu device={d} onRename={() => setRenaming(true)} onChanged={onChanged} />
        </div>
      </div>

      <div className="metrics">
        <Metric icon={<Icon.Wifi />} label="Connection">
          <span className={d.online ? "good" : "muted"}>{d.online ? "Online" : "Offline"}</span>
        </Metric>
        <Metric icon={<Icon.Wave size={22} />} label="Audio level">
          <LevelBars dbfs={d.online ? d.status?.noise_floor_dbfs ?? null : null} />
        </Metric>
        {/* Placeholder: devices do not report storage yet. */}
        <Metric icon={<Icon.Storage />} label="Storage">
          <span className="metric-inline"><span className="muted">Not reported yet</span><span className="meter-bar"><span style={{ width: "0%" }} /></span></span>
        </Metric>
        <Metric icon={<Icon.Info />} label="Last heard">
          {d.last_seen_at ? fmtAgo(d.last_seen_at) : "never"}
        </Metric>
      </div>

      <div className="device-card-links">
        <Link to={`/diary?device=${d.id}`}><Icon.Activity /> View recent activity <Icon.Chevron /></Link>
        <span className="sep" />
        <Link to="/guide"><Icon.Book size={20} /> Device guide <Icon.Chevron /></Link>
      </div>
    </article>
  );
}

function Metric({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="metric">
      <span className="metric-icon">{icon}</span>
      <span>
        <span className="muted small">{label}</span>
        <span className="metric-value">{children}</span>
      </span>
    </div>
  );
}

/** The background level the device reports in its heartbeat, as 8 bars
 *  (−80 dBFS → none, −30 dBFS → all). */
function LevelBars({ dbfs }: { dbfs: number | null }) {
  const lit = dbfs == null ? 0 : Math.max(0, Math.min(8, Math.round(((dbfs + 80) / 50) * 8)));
  return (
    <span className="level-bars" title={dbfs == null ? "No reading" : `${dbfs.toFixed(1)} dBFS background`}>
      {Array.from({ length: 8 }, (_, i) => <span key={i} className={i < lit ? "lit" : ""} />)}
    </span>
  );
}

function HelpTile({ to, icon, title, children }: { to: string; icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <Link to={to} className="help-tile">
      <span className="stat-icon round">{icon}</span>
      <span className="help-tile-text">
        <strong>{title}</strong>
        <span className="muted small">{children}</span>
      </span>
      <Icon.Chevron />
    </Link>
  );
}

function RenameForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const [name, setName] = useState(device.name ?? "");
  async function save(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) await api.patch(`/api/devices/${device.id}`, { name: name.trim() });
    onDone();
  }
  return (
    <form className="rename" onSubmit={save}>
      <input autoFocus value={name} maxLength={100} onChange={(e) => setName(e.target.value)} aria-label="Device name" />
      <button className="cta cta-small">Save</button>
      <button type="button" className="link" onClick={onDone}>Cancel</button>
    </form>
  );
}

function DeviceMenu({ device, onRename, onChanged }: { device: Device; onRename: () => void; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
        setConfirming(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  async function remove() {
    await api.delete(`/api/devices/${device.id}`);
    setOpen(false);
    onChanged();
  }

  return (
    <div className="kebab" ref={ref}>
      <button className="ghost square" aria-label="Device options" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
        </svg>
      </button>
      {open && (
        <div className="menu" role="menu">
          {confirming ? (
            <>
              <div className="menu-email">Remove {device.name ?? device.id} from your account? Its history is kept.</div>
              <button role="menuitem" className="danger" onClick={remove}>Yes, remove it</button>
              <button role="menuitem" onClick={() => setConfirming(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button role="menuitem" onClick={() => navigate(`/devices/${device.id}`)}>Open</button>
              <button role="menuitem" onClick={() => navigate(`/diary?device=${device.id}`)}>Diary</button>
              <button role="menuitem" onClick={() => { setOpen(false); onRename(); }}>Rename</button>
              <button role="menuitem" className="danger" onClick={() => setConfirming(true)}>Remove…</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
